import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import * as yup from 'yup';
import { Formik } from 'formik';
import { useEmployeeAttendanceAuth } from '../context/EmployeeAttendanceAuthContext';
import { getPostLoginPath, getVisiblePostLoginPath } from '../utils/roleAccess';
import { MdVisibility, MdVisibilityOff } from 'react-icons/md';
import '../employeeAttendance.css';

const LOGO =
  'https://bu-page-assets.s3.ap-northeast-1.amazonaws.com/static-assets/others/Benadir University Logo.png';

const isLocalHost = ['localhost', '127.0.0.1'].includes(window.location.hostname);

function EmployeeAttendanceLogin() {
  const navigate = useNavigate();
  const {
    login,
    isAuthenticated,
    user,
    error: authError,
    clearError,
  } = useEmployeeAttendanceAuth();
  const [error, setError] = useState(null);
  const [showPassword, setShowPassword] = useState(false);

  const validationSchema = yup.object({
    email: yup.string().email('Enter a valid email').required('Email is required'),
    password: yup.string().min(5, 'Minimum 5 characters').required('Password is required'),
  });

  // Only skip the form when the user has a dashboard to land on. A signed-in
  // account with no dashboard role resolves to "/", which would bounce them
  // straight back to the website, so they stay on the login form instead.
  useEffect(() => {
    if (!isAuthenticated) return;
    const landing = getPostLoginPath(user);
    if (landing !== '/') {
      navigate(landing, { replace: true });
    }
  }, [isAuthenticated, user, navigate]);

  useEffect(() => {
    clearError();
    setError(null);
  }, [clearError]);

  const handleSubmit = async (values, actions) => {
    try {
      actions.setSubmitting(true);
      setError(null);
      clearError();

      const result = await login(values.email, values.password);
      if (result?.success) {
        const destination = getVisiblePostLoginPath(result.user, result.landingPath);
        if (isLocalHost && destination === '/') {
          setError('This local Odoo account has no enabled dashboard role. Sign in with an HR, Admission, Exam, Postgraduate, Tutor, or Faculty Admin account.');
          return;
        }
        navigate(destination, { replace: true });
      } else {
        setError(result?.error || 'Login failed');
      }
    } catch (err) {
      setError(err.message || 'Network error');
    } finally {
      actions.setSubmitting(false);
    }
  };

  const displayError = error || authError;

  return (
    <div className="ea-app ea-login-page">
      <div className="ea-login-card">
        <div className="ea-login-head">
          <a href="/">
            <img className="h-11 mx-auto" src={LOGO} alt="Benadir University" />
          </a>
          <h2 className="ea-greeting mt-5" style={{ fontSize: 18 }}>
            BU Dashboard
          </h2>
          <p className="ea-dateline mt-1">Sign in with your portal credentials</p>
        </div>

        <div className="ea-login-body">
          {displayError && (
            <div
              className="mb-4 p-3 rounded-lg text-sm"
              style={{
                background: '#fee2e2',
                color: '#991b1b',
                border: '0.5px solid rgba(153,27,27,0.15)',
              }}
            >
              {displayError}
            </div>
          )}

          <Formik
            initialValues={{ email: '', password: '' }}
            validationSchema={validationSchema}
            onSubmit={handleSubmit}
          >
            {({
              values,
              errors,
              touched,
              handleChange,
              handleBlur,
              handleSubmit,
              isSubmitting,
            }) => (
              <form onSubmit={handleSubmit}>
                <label className="block mb-4" htmlFor="email">
                  <span className="ea-field-label">Email</span>
                  <input
                    id="email"
                    name="email"
                    type="email"
                    placeholder="you@university.edu"
                    value={values.email}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    disabled={isSubmitting}
                    className="ea-field-input"
                  />
                  {touched.email && errors.email && (
                    <p className="text-red-600 text-xs mt-1">{errors.email}</p>
                  )}
                </label>

                <label className="block mb-4" htmlFor="password">
                  <span className="ea-field-label">Password</span>
                  <div style={{ position: 'relative' }}>
                    <input
                      id="password"
                      name="password"
                      type={showPassword ? 'text' : 'password'}
                      placeholder="Enter password"
                      value={values.password}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      disabled={isSubmitting}
                      className="ea-field-input"
                      style={{ paddingRight: 48 }}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword((visible) => !visible)}
                      disabled={isSubmitting}
                      aria-label={showPassword ? 'Hide password' : 'Show password'}
                      title={showPassword ? 'Hide password' : 'Show password'}
                      style={{
                        position: 'absolute',
                        right: 14,
                        top: '50%',
                        transform: 'translateY(-50%)',
                        display: 'grid',
                        placeItems: 'center',
                        color: '#64748b',
                        background: 'transparent',
                        border: 0,
                        cursor: 'pointer',
                      }}
                    >
                      {showPassword ? <MdVisibilityOff size={22} /> : <MdVisibility size={22} />}
                    </button>
                  </div>
                  {touched.password && errors.password && (
                    <p className="text-red-600 text-xs mt-1">{errors.password}</p>
                  )}
                </label>

                <button type="submit" disabled={isSubmitting} className="ea-btn-submit">
                  {isSubmitting ? 'Signing in...' : 'Sign In'}
                </button>
              </form>
            )}
          </Formik>

          <p className="text-center mt-6">
            <a href="/" className="ea-dateline" style={{ textDecoration: 'none' }}>
              Back to main site
            </a>
          </p>
        </div>
      </div>
    </div>
  );
}

export default EmployeeAttendanceLogin;
